import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, RefreshCw, Settings, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/empty-state";
import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { apiFetch } from "@/lib/api";
import type { AssetRow } from "@/lib/master-data";
import { formatDateTime } from "@/lib/utils";

interface DisposalRow {
  id: string;
  assetId: string;
  assetCode: string;
  materialName: string;
  reason: string;
  method: string;
  status: string;
  requestedByName: string | null;
  decisionNote: string | null;
  createdAt: string;
  decidedAt: string | null;
}

const METHOD_LABELS: Record<string, string> = {
  sold: "出售",
  recycled: "回收",
  scrapped: "报废",
  other: "其他",
};

export function DisposalsPage() {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [assetId, setAssetId] = useState("");
  const [reason, setReason] = useState("");
  const [method, setMethod] = useState("recycled");
  const [busy, setBusy] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [requireSeparate, setRequireSeparate] = useState(false);
  const settingsQuery = useQuery({
    queryKey: ["disposal-settings"],
    queryFn: () =>
      apiFetch<{ requireSeparateApprover: boolean }>("/api/v1/admin/settings/disposal"),
  });
  const listQuery = useQuery({
    queryKey: ["disposals"],
    queryFn: () => apiFetch<DisposalRow[]>("/api/v1/disposals"),
  });
  const assetsQuery = useQuery({
    queryKey: ["disposal-assets"],
    queryFn: () => apiFetch<AssetRow[]>("/api/v1/assets?status=in_stock"),
  });

  async function decide(id: string, decision: "approve" | "reject") {
    try {
      await apiFetch(`/api/v1/disposals/${id}/decide`, {
        method: "POST",
        body: JSON.stringify({ decision }),
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["disposals"] }),
        queryClient.invalidateQueries({ queryKey: ["assets"] }),
      ]);
      toast.success(decision === "approve" ? "处置已通过，资产已标记为已处置" : "处置已拒绝");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "处理失败");
    }
  }

  async function create() {
    if (!assetId) return toast.error("请选择资产");
    if (reason.trim().length < 2) return toast.error("请填写处置原因");
    setBusy(true);
    try {
      await apiFetch("/api/v1/disposals", {
        method: "POST",
        body: JSON.stringify({ assetId, reason: reason.trim(), method }),
      });
      await queryClient.invalidateQueries({ queryKey: ["disposals"] });
      toast.success("处置申请已创建");
      setCreateOpen(false);
      setAssetId("");
      setReason("");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "创建失败");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="处置审批"
        description="资产处置需独立申请与审批；通过后资产状态变为已处置，不再允许领用。"
        actions={
          <>
            <Button variant="outline" onClick={() => listQuery.refetch()}>
              <RefreshCw className="h-4 w-4" />
              刷新
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setRequireSeparate(settingsQuery.data?.requireSeparateApprover ?? false);
                setSettingsOpen(true);
              }}
            >
              <Settings className="h-4 w-4" />
              处置设置
            </Button>
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" />
              申请处置
            </Button>
          </>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>处置单</CardTitle>
          <CardDescription>最近 100 张；待审批资产不能重复申请。</CardDescription>
        </CardHeader>
        <CardContent>
          {listQuery.isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : (listQuery.data ?? []).length === 0 ? (
            <EmptyState title="没有处置单" description="从在库资产发起处置申请。" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>资产</TableHead>
                  <TableHead>原因</TableHead>
                  <TableHead>方式</TableHead>
                  <TableHead>申请人</TableHead>
                  <TableHead>状态</TableHead>
                  <TableHead>申请时间</TableHead>
                  <TableHead className="text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(listQuery.data ?? []).map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <span className="font-mono text-xs">{row.assetCode}</span>
                      <span className="ml-2 text-xs text-muted-foreground">{row.materialName}</span>
                    </TableCell>
                    <TableCell className="max-w-64 truncate">{row.reason}</TableCell>
                    <TableCell>{METHOD_LABELS[row.method] ?? row.method}</TableCell>
                    <TableCell>{row.requestedByName ?? "—"}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          row.status === "completed"
                            ? "success"
                            : row.status === "rejected"
                              ? "danger"
                              : "warning"
                        }
                      >
                        {row.status === "completed"
                          ? "已通过"
                          : row.status === "rejected"
                            ? "已拒绝"
                            : "待审批"}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatDateTime(row.createdAt)}</TableCell>
                    <TableCell className="text-right">
                      {row.status === "pending" ? (
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => decide(row.id, "approve")}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            通过
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => decide(row.id, "reject")}
                          >
                            拒绝
                          </Button>
                        </div>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>处置设置</DialogTitle>
            <DialogDescription>控制申请人能否审批自己的处置单。</DialogDescription>
          </DialogHeader>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={requireSeparate}
              onChange={(event) => setRequireSeparate(event.target.checked)}
            />
            要求申请人与审批人分离
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)}>
              取消
            </Button>
            <Button
              onClick={async () => {
                await apiFetch("/api/v1/admin/settings/disposal", {
                  method: "PUT",
                  body: JSON.stringify({ requireSeparateApprover: requireSeparate }),
                });
                await settingsQuery.refetch();
                setSettingsOpen(false);
              }}
            >
              保存
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>申请资产处置</DialogTitle>
            <DialogDescription>处置通过后资产进入已处置状态，操作不可逆。</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>资产</Label>
              <select
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={assetId}
                onChange={(event) => setAssetId(event.target.value)}
              >
                <option value="">选择在库资产</option>
                {(assetsQuery.data ?? []).map((asset) => (
                  <option key={asset.id} value={asset.id}>
                    {asset.assetCode} · {asset.serialNumber ?? "无序列号"}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>处置方式</Label>
              <select
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={method}
                onChange={(event) => setMethod(event.target.value)}
              >
                <option value="recycled">回收</option>
                <option value="sold">出售</option>
                <option value="scrapped">报废</option>
                <option value="other">其他</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>处置原因</Label>
              <Input
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="如：设备老化，无维修价值"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              取消
            </Button>
            <Button onClick={create} disabled={busy}>
              提交申请
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
